import type {
  BatchDraftItem,
  BatchDraftExport,
  BatchDraftExportV1,
  BatchDraftInstance,
  BatchDraftItemV1,
} from '../types/batch-entry';
import {
  canMergeItems,
  getTotalInstanceQuantity,
  isBatchDraftItemV1,
  migrateBatchDraftItemV1ToV2,
  BATCH_DRAFT_EXPIRY_MS,
} from '../types/batch-entry';

/**
 * Input type for creating a draft item.
 * Omits auto-generated fields (clientId, addedAt, instances) and deprecated fields.
 */
export type CreateDraftItemInput = Omit<
  BatchDraftItem,
  'clientId' | 'addedAt' | 'instances' | 'quantity' | 'expiryDate' | 'notes'
> & {
  quantity?: number;
  expiryDate?: string;
  notes?: string;
};

/**
 * Create a new BatchDraftItem from product input.
 * Returns the new item ready to be appended to the store's items array.
 *
 * @param input - Product data for the draft item
 * @param generateId - Function to generate unique IDs (e.g., nanoid)
 * @returns A tuple of [newItem, clientId] for the store to use
 */
export function createDraftItem(
  input: CreateDraftItemInput,
  generateId: () => string
): [BatchDraftItem, string] {
  const clientId = generateId();
  const instanceId = generateId();
  const now = Date.now();

  const quantity = input.quantity ?? 1;
  const expiryDate = input.expiryDate;
  const notes = input.notes;

  const newItem: BatchDraftItem = {
    ...input,
    clientId,
    addedAt: now,
    instances: [
      {
        instanceId,
        quantity,
        expiryDate,
        notes,
        addedAt: now,
      },
    ],
    // Keep deprecated fields synced for backwards compatibility
    quantity,
    expiryDate,
    notes,
  };

  return [newItem, clientId];
}

/**
 * Apply updates to a draft item in an items array.
 * Handles instance syncing for backwards compatibility.
 *
 * @param items - Current items array
 * @param clientId - ID of the item to update
 * @param updates - Partial updates to apply
 * @returns New items array with the update applied
 */
export function updateDraftItem(
  items: BatchDraftItem[],
  clientId: string,
  updates: Partial<BatchDraftItem>
): BatchDraftItem[] {
  return items.map((item) => {
    if (item.clientId !== clientId) return item;

    const updatedItem = { ...item, ...updates };

    // Sync item-level fields with instances for backwards compatibility
    if (updatedItem.instances && updatedItem.instances.length > 0) {
      if (updatedItem.instances.length === 1) {
        const hasQuantityUpdate = updates.quantity !== undefined;
        const hasExpiryUpdate = updates.expiryDate !== undefined;
        const hasNotesUpdate = updates.notes !== undefined;

        if (hasQuantityUpdate || hasExpiryUpdate || hasNotesUpdate) {
          updatedItem.instances = [
            {
              ...updatedItem.instances[0],
              ...(hasQuantityUpdate && { quantity: updates.quantity }),
              ...(hasExpiryUpdate && { expiryDate: updates.expiryDate }),
              ...(hasNotesUpdate && { notes: updates.notes }),
            },
          ];
        }
      } else if (updates.expiryDate !== undefined || updates.notes !== undefined) {
        // Multiple instances: only sync expiryDate/notes to first instance
        updatedItem.instances = [...updatedItem.instances];
        updatedItem.instances[0] = {
          ...updatedItem.instances[0],
          ...(updates.expiryDate !== undefined && { expiryDate: updates.expiryDate }),
          ...(updates.notes !== undefined && { notes: updates.notes }),
        };
      }

      // Always recalculate total quantity from instances
      updatedItem.quantity = getTotalInstanceQuantity(updatedItem);
    }

    return updatedItem;
  });
}

/**
 * Remove a draft item from the items array.
 * Also removes the item from the selected set if present.
 *
 * @param items - Current items array
 * @param selectedClientIds - Current selection set
 * @param clientId - ID of the item to remove
 * @returns New items array and updated selection set
 */
export function removeDraftItem(
  items: BatchDraftItem[],
  selectedClientIds: Set<string>,
  clientId: string
): { items: BatchDraftItem[]; selectedClientIds: Set<string> } {
  const newSelectedIds = new Set(selectedClientIds);
  newSelectedIds.delete(clientId);
  return {
    items: items.filter((item) => item.clientId !== clientId),
    selectedClientIds: newSelectedIds,
  };
}

/**
 * Increment the first instance's quantity for an item matching the given barcode.
 *
 * @param items - Current items array
 * @param barcode - Barcode to match
 * @param quantity - Amount to increment (default 1)
 * @returns New items array (unchanged if no match), and whether a match was found
 */
export function incrementItemByBarcode(
  items: BatchDraftItem[],
  barcode: string,
  quantity = 1
): { items: BatchDraftItem[]; found: boolean } {
  const existingItem = items.find((item) => item.barcode === barcode);
  if (!existingItem || !existingItem.instances || existingItem.instances.length === 0) {
    return { items, found: false };
  }

  const newItems = items.map((item) => {
    if (item.clientId !== existingItem.clientId) return item;
    const updatedInstances = [...item.instances];
    updatedInstances[0] = {
      ...updatedInstances[0],
      quantity: updatedInstances[0].quantity + quantity,
    };
    return {
      ...item,
      instances: updatedInstances,
      quantity: item.quantity + quantity,
    };
  });

  return { items: newItems, found: true };
}

/**
 * Add a new instance to an existing item matching the given barcode.
 *
 * @param items - Current items array
 * @param barcode - Barcode to match
 * @param generateId - Function to generate unique IDs
 * @param quantity - Quantity for the new instance (default 1)
 * @returns New items array, and the clientId of the matched item (null if no match)
 */
export function addInstanceByBarcode(
  items: BatchDraftItem[],
  barcode: string,
  generateId: () => string,
  quantity = 1
): { items: BatchDraftItem[]; clientId: string | null } {
  const existingItem = items.find((item) => item.barcode === barcode);
  if (!existingItem) {
    return { items, clientId: null };
  }

  const instanceId = generateId();
  const now = Date.now();
  const newItems = items.map((item) => {
    if (item.clientId !== existingItem.clientId) return item;
    const newInstance: BatchDraftInstance = {
      instanceId,
      quantity,
      addedAt: now,
    };
    const updatedInstances = [...item.instances, newInstance];
    return {
      ...item,
      instances: updatedInstances,
      quantity: updatedInstances.reduce((sum, inst) => sum + inst.quantity, 0),
    };
  });

  return { items: newItems, clientId: existingItem.clientId };
}

/**
 * Toggle an item's selection state.
 *
 * @param selected - Current selection set
 * @param clientId - ID to toggle
 * @returns New selection set
 */
export function toggleItemSelection(selected: Set<string>, clientId: string): Set<string> {
  const newSelected = new Set(selected);
  if (newSelected.has(clientId)) {
    newSelected.delete(clientId);
  } else {
    newSelected.add(clientId);
  }
  return newSelected;
}

/**
 * Create a selection set containing all item IDs.
 *
 * @param items - Items to select
 * @returns Set of all item clientIds
 */
export function selectAllItems(items: BatchDraftItem[]): Set<string> {
  return new Set(items.map((item) => item.clientId));
}

/**
 * Merge selected items into the first selected item.
 * All instances from selected items are combined into the first item,
 * and the other selected items are removed.
 *
 * @param items - Current items array
 * @param selectedClientIds - Set of selected item IDs
 * @returns New items array and success flag, or null if merge is not possible
 */
export function mergeSelectedItems(
  items: BatchDraftItem[],
  selectedClientIds: Set<string>
): { items: BatchDraftItem[]; success: boolean } {
  const selectedItems = items.filter((item) => selectedClientIds.has(item.clientId));

  if (selectedItems.length < 2) {
    return { items, success: false };
  }

  // Check if all selected items can be merged
  const firstItem = selectedItems[0];
  const allCanMerge = selectedItems.every((item) => canMergeItems(firstItem, item));

  if (!allCanMerge) {
    return { items, success: false };
  }

  // Merge all instances into the first item
  const mergedInstances: BatchDraftInstance[] = [];
  for (const item of selectedItems) {
    mergedInstances.push(...item.instances);
  }

  // Sort instances by addedAt
  mergedInstances.sort((a, b) => a.addedAt - b.addedAt);

  // Remove other selected items, update first with merged instances
  const otherClientIds = new Set(selectedItems.slice(1).map((item) => item.clientId));

  const newItems = items
    .filter((item) => !otherClientIds.has(item.clientId))
    .map((item) => {
      if (item.clientId === firstItem.clientId) {
        return {
          ...item,
          instances: mergedInstances,
          quantity: mergedInstances.reduce((sum, inst) => sum + inst.quantity, 0),
        };
      }
      return item;
    });

  return { items: newItems, success: true };
}

/**
 * Create an export data object from current items and storage location.
 *
 * @param items - Current draft items
 * @param defaultStorageLocationId - Default storage location
 * @returns BatchDraftExport ready for serialization
 */
export function exportDraftData(
  items: BatchDraftItem[],
  defaultStorageLocationId: string | null
): BatchDraftExport {
  return {
    items,
    defaultStorageLocationId,
    expiresAt: Date.now() + BATCH_DRAFT_EXPIRY_MS,
    version: 2 as const,
  };
}

/**
 * Parse and migrate imported draft data.
 * Handles v1 to v2 migration and expiry validation.
 * Returns null if data is expired.
 *
 * @param data - Raw draft data (v1 or v2 format)
 * @param generateId - Function to generate unique IDs for migrated items
 * @returns Parsed items and storage location, or null if expired
 */
export function parseDraftImport(
  data: BatchDraftExport | BatchDraftExportV1,
  generateId: () => string
): { items: BatchDraftItem[]; defaultStorageLocationId: string | null } | null {
  // Check if draft has expired
  if (data.expiresAt < Date.now()) {
    return null;
  }

  // Migrate v1 items to v2 if needed
  let items: BatchDraftItem[];
  if (data.version === 1 || (data.items.length > 0 && isBatchDraftItemV1(data.items[0]))) {
    items = (data.items as BatchDraftItemV1[]).map((item) =>
      migrateBatchDraftItemV1ToV2(item, generateId)
    );
  } else {
    items = data.items as BatchDraftItem[];
  }

  return { items, defaultStorageLocationId: data.defaultStorageLocationId };
}

/**
 * Validate raw data as a BatchDraftExport.
 * Checks version, expiry, and that items exist.
 *
 * @param data - Raw parsed data to validate
 * @returns Whether the data is a valid, non-expired draft export
 */
export function validateDraftExport(data: unknown): data is BatchDraftExport {
  if (typeof data !== 'object' || data === null) return false;
  const obj = data as Record<string, unknown>;

  // Validate version (accept both v1 and v2)
  if (obj.version !== 1 && obj.version !== 2) return false;

  // Check expiry
  if (typeof obj.expiresAt !== 'number' || obj.expiresAt < Date.now()) return false;

  // Validate has items
  if (!Array.isArray(obj.items) || obj.items.length === 0) return false;

  return true;
}

/**
 * Add a new instance to an existing item.
 *
 * @param items - Current items array
 * @param clientId - ID of the item to add instance to
 * @param instance - Instance data (without instanceId)
 * @param generateId - Function to generate unique IDs
 * @returns New items array and the generated instanceId
 */
export function addInstanceToItem(
  items: BatchDraftItem[],
  clientId: string,
  instance: Omit<BatchDraftInstance, 'instanceId'>,
  generateId: () => string
): { items: BatchDraftItem[]; instanceId: string } {
  const instanceId = generateId();
  const newItems = items.map((item) => {
    if (item.clientId !== clientId) return item;
    const newInstance: BatchDraftInstance = {
      ...instance,
      instanceId,
      addedAt: instance.addedAt ?? Date.now(),
    };
    const updatedInstances = [...item.instances, newInstance];
    return {
      ...item,
      instances: updatedInstances,
      quantity: updatedInstances.reduce((sum, inst) => sum + inst.quantity, 0),
    };
  });

  return { items: newItems, instanceId };
}

/**
 * Remove a specific instance from an item.
 * If it's the last instance, removes the entire item.
 *
 * @param items - Current items array
 * @param selectedClientIds - Current selection set
 * @param clientId - ID of the item
 * @param instanceId - ID of the instance to remove
 * @returns New items array and updated selection set
 */
export function removeInstance(
  items: BatchDraftItem[],
  selectedClientIds: Set<string>,
  clientId: string,
  instanceId: string
): { items: BatchDraftItem[]; selectedClientIds: Set<string> } {
  const item = items.find((i) => i.clientId === clientId);
  if (!item) {
    return { items, selectedClientIds };
  }

  // If this is the last instance, remove the entire item
  if (item.instances.length <= 1) {
    return removeDraftItem(items, selectedClientIds, clientId);
  }

  // Otherwise, just remove the instance
  const newItems = items.map((i) => {
    if (i.clientId !== clientId) return i;
    const updatedInstances = i.instances.filter((inst) => inst.instanceId !== instanceId);
    return {
      ...i,
      instances: updatedInstances,
      quantity: updatedInstances.reduce((sum, inst) => sum + inst.quantity, 0),
    };
  });

  return { items: newItems, selectedClientIds };
}

/**
 * Update a specific instance within an item.
 *
 * @param items - Current items array
 * @param clientId - ID of the item
 * @param instanceId - ID of the instance to update
 * @param updates - Partial updates to apply to the instance
 * @returns New items array
 */
export function updateInstance(
  items: BatchDraftItem[],
  clientId: string,
  instanceId: string,
  updates: Partial<BatchDraftInstance>
): BatchDraftItem[] {
  return items.map((item) => {
    if (item.clientId !== clientId) return item;
    const updatedInstances = item.instances.map((inst) =>
      inst.instanceId === instanceId ? { ...inst, ...updates } : inst
    );
    return {
      ...item,
      instances: updatedInstances,
      quantity: updatedInstances.reduce((sum, inst) => sum + inst.quantity, 0),
    };
  });
}
